import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Receipt, ExternalLink } from "lucide-react";

export default function AccountInvoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null); // {id, html}

  useEffect(() => {
    api.get("/account/invoices")
      .then((r) => { setItems(r.data); })
      .finally(() => setLoading(false));
  }, []);

  const openInvoice = async (inv) => {
    try {
      const { data } = await api.get(`/account/invoices/${inv.id}/html`, { responseType: "text" });
      setViewing({ id: inv.id, order_number: inv.order_number, html: data });
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="account-invoices">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl tracking-tight">Invoices</h2>
      </div>
      {loading && <p className="text-sm text-[var(--ink-muted)]">Loading…</p>}
      {!loading && items.length === 0 && (
        <div className="border border-dashed border-[var(--line)] rounded-md p-8 text-center text-sm text-[var(--ink-muted)]" data-testid="invoices-empty">
          No invoices yet. When you place an order, an invoice will be saved here and sent to your email.
        </div>
      )}
      {items.length > 0 && (
        <div className="border border-[var(--line)] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--paper-2)]">
              <tr>
                <th className="text-left px-4 py-3 font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)]">Order #</th>
                <th className="text-left px-4 py-3 font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)]">Date</th>
                <th className="text-left px-4 py-3 font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)]">Status</th>
                <th className="text-right px-4 py-3 font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)]">Total</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => (
                <tr key={inv.id} className="border-t border-[var(--line)]" data-testid={`invoice-row-${inv.order_number}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold">{inv.order_number}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--ink-soft)]">{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-[var(--ink-soft)]">{inv.status || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-right">${Number(inv.total_usd || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openInvoice(inv)}
                      className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--terra)] hover:underline"
                      data-testid={`view-invoice-${inv.order_number}`}
                    >
                      <Receipt className="h-3.5 w-3.5" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewing && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex flex-col sm:p-4 lg:p-8"
          onClick={() => setViewing(null)}
          data-testid="invoice-viewer"
        >
          <div
            className="bg-white border border-[var(--line)] w-full h-full sm:rounded-md sm:mx-auto sm:max-w-5xl flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-[var(--line)] flex items-center justify-between bg-white shrink-0">
              <div className="font-mono text-xs uppercase tracking-wider">Invoice · {viewing.order_number}</div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    // Open the invoice HTML in a new tab so the browser's own
                    // print dialog gets the full document — not the modal chrome.
                    const w = window.open("", "_blank");
                    if (w) { w.document.write(viewing.html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
                  }}
                  className="text-[11px] font-mono uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--terra)] inline-flex items-center gap-1"
                  data-testid="invoice-print"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Print / Save PDF
                </button>
                <button
                  onClick={() => setViewing(null)}
                  className="text-[var(--ink-muted)] hover:text-[var(--ink)] text-lg leading-none px-2"
                  aria-label="Close"
                  data-testid="invoice-close"
                >
                  ✕
                </button>
              </div>
            </div>
            <iframe
              title={`Invoice ${viewing.order_number}`}
              srcDoc={viewing.html}
              className="w-full bg-white border-0"
              style={{ flex: "1 1 auto", minHeight: 0, height: "100%" }}
              data-testid="invoice-iframe"
              sandbox=""
            />
          </div>
        </div>
      )}
    </div>
  );
}
